<template>
  <teleport to="body">
    <transition name="add-data-drawer">
      <div v-if="modelValue" class="fixed inset-0 z-[1]">
        <div class="absolute inset-0 bg-black/40" @click="close"></div>
        <div class="add-data-drawer-panel absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-lg font-medium text-primary-3">เพิ่มข้อมูลจากคำถาม</h3>
            <button type="button" class="text-gray-400 hover:text-gray-600" @click="close">
              <XIcon class="w-5 h-5" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div class="mt-2 col-span-12">
              <label class="from-label text-medium text-gray-600" for="dataset-select">ชุดข้อมูล<span class="text-red-500">*</span></label>
              <v-select
                data-test="dataset-select"
                id="dataset-select"
                v-model="datasetComputed"
                :options="datasets"
                label="label"
                :clearable="true"
                placeholder="กรุณาเลือกชุดข้อมูล"
                :appendToBody="true"
              >
                <template #no-options="">
                  <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
                </template>
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
                </template>
              </v-select>
            </div>
            <div class="mt-2 col-span-12">
              <label class="from-label text-medium text-gray-600" for="section-select">ส่วนข้อมูล<span class="text-red-500">*</span></label>
              <v-select
                data-test="section-select"
                id="section-select"
                v-model="sectionComputed"
                :options="sectionOptions"
                label="label"
                :clearable="true"
                :disabled="sectionOptions.length === 0"
                placeholder="กรุณาเลือกส่วนข้อมูล"
                :appendToBody="true"
              >
                <template #no-options="">
                  <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
                </template>
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
                </template>
              </v-select>
            </div>

            <div class="mt-2 col-span-12">
              <label class="from-label text-medium text-gray-600" for="question-select">คำถาม<span class="text-red-500">*</span></label>
              <v-select
                data-test="question-select"
                id="question-select"
                v-model="questionComputed"
                :options="questionOptions"
                label="label"
                :clearable="true"
                :disabled="questionOptions.length === 0"
                placeholder="กรุณาเลือกคำถาม"
                :appendToBody="true"
              >
                <template #no-options="">
                  <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
                </template>
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5" /></span>
                </template>
              </v-select>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              :disabled="!canAdd"
              class="inline-flex items-center gap-1.5 rounded-md bg-primary-1 text-white px-6 py-2.5 text-sm hover:bg-primary-3 disabled:opacity-40 disabled:cursor-not-allowed"
              @click="add"
            >
              เพิ่มข้อมูล
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { reactive, computed } from "vue";
import { useStore } from "vuex";

defineProps({
  modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "add"]);

const store = useStore();

const INIT_DATA_QUERY = { dataset: null, section: null, question: null };

const filterState = reactive(
  JSON.parse(JSON.stringify(store.getters["dashboardOverviewFilter/dataQuery"] || INIT_DATA_QUERY))
);

const datasets = [
  {
    label: "ครัวเรือน",
    value: "household",
    sections: [
      {
        label: "คุณภาพชีวิตความเป็นอยู่",
        value: "quality",
        questions: [
          { label: "ประเภทที่อยู่อาศัย", value: "home_type", unit: "ครัวเรือน", categories: ["บ้านเดี่ยว", "ห้องแถว/ตึกแถว/อาคารพาณิชย์", "ทาวน์เฮ้าส์/บ้านแฝด", "ห้องชุด", "เพิงพักพิงชั่วคราว", "อื่นๆ"] },
          { label: "สถานภาพการครอบครองที่อยู่อาศัย", value: "ownership", unit: "ครัวเรือน", categories: ["เป็นเจ้าของ", "เช่า", "อยู่กับผู้อื่น", "บ้านพักสวัสดิการ", "ปลูกในที่ดินผู้อื่น", "อื่นๆ"] },
          { label: "แหล่งน้ำกิน น้ำใช้", value: "water", unit: "ครัวเรือน", categories: ["ประปา", "แหล่งน้ำธรรมชาติ", "น้ำบรรจุขวด", "บ่อบาดาล", "อื่นๆ"] },
        ],
      },
      {
        label: "ความมั่นคงทางเศรษฐกิจ",
        value: "economic",
        questions: [
          { label: "ระดับรายได้ต่อครัวเรือน", value: "income", unit: "ครัวเรือน", categories: ["< 5,000", "5,000-10,000", "10,000-20,000", "20,000-30,000", "30,000-50,000", "> 50,000"] },
          { label: "สถานะหนี้สิน", value: "debt", unit: "ครัวเรือน", categories: ["ไม่มีหนี้สิน", "หนี้ในระบบ", "หนี้นอกระบบ", "ทั้งในและนอกระบบ"] },
        ],
      },
    ],
  },
  {
    label: "บุคคล",
    value: "person",
    sections: [
      {
        label: "การศึกษาและทักษะที่จำเป็น",
        value: "education",
        questions: [
          { label: "ระดับการศึกษาสูงสุด", value: "edu_level", unit: "คน", categories: ["ประถมศึกษา", "มัธยมต้น", "มัธยมปลาย", "ปวช./ปวส.", "ปริญญาตรี", "สูงกว่าปริญญาตรี"] },
          { label: "สถานะการศึกษา", value: "edu_status", unit: "คน", categories: ["กำลังเรียน", "จบการศึกษา", "ไม่ได้เรียน"] },
        ],
      },
      {
        label: "ข้อมูลทั่วไป",
        value: "general",
        questions: [
          { label: "บัตรประชาชน", value: "id_card", unit: "คน", categories: ["มีบัตรประชาชน", "ไม่มีบัตรประชาชน"] },
          { label: "สถานะภาพสมรส", value: "marital", unit: "คน", categories: ["โสด", "สมรส", "หย่าร้าง", "หม้าย", "อื่นๆ"] },
        ],
      },
    ],
  },
  {
    label: "NEETs",
    value: "neets",
    sections: [
      {
        label: "ภาพรวม",
        value: "overview",
        questions: [
          { label: "ระดับการศึกษาสูงสุด", value: "neets_edu", unit: "คน", categories: ["ม.ต้น/ต่ำกว่า", "ม.ปลาย", "ปวช./ปวส.", "ป.ตรี", "ไม่เคยเรียน"] },
          { label: "ความมุ่งหวังต่ออนาคต", value: "neets_aspire", unit: "คน", categories: ["เรียนต่อ", "ฝึกอบรม+ทำงาน", "ฝึกอบรม", "ทำงานทันที", "ยังไม่ตัดสินใจ"] },
        ],
      },
    ],
  },
  {
    label: "เด็กพิการ",
    value: "disability",
    sections: [
      {
        label: "สถานะทางสุขภาพ",
        value: "health",
        questions: [
          { label: "กลุ่มโรคประจำตัว", value: "disease_group", unit: "คน", categories: ["ระบบประสาท", "NCDs", "เบาหวาน", "โรคติดเชื้อ", "บาดเจ็บ", "อื่นๆ"] },
        ],
      },
    ],
  },
];

const datasetComputed = computed({
  get() { return filterState.dataset; },
  set(val) {
    filterState.dataset = val;
    filterState.section = null;
    filterState.question = null;
  },
});

const sectionOptions = computed(() => filterState.dataset?.sections || []);

const sectionComputed = computed({
  get() { return filterState.section; },
  set(val) {
    filterState.section = val;
    filterState.question = null;
  },
});

const questionOptions = computed(() => filterState.section?.questions || []);

const questionComputed = computed({
  get() { return filterState.question; },
  set(val) { filterState.question = val; },
});

const canAdd = computed(() => !!filterState.question);

const close = () => emit("update:modelValue", false);

const add = () => {
  if (!canAdd.value) return;
  store.dispatch("dashboardOverviewFilter/setStore", { dataQuery: JSON.parse(JSON.stringify(filterState)) });
  emit("add", {
    datasetLabel: filterState.dataset.label,
    sectionLabel: filterState.section.label,
    title: filterState.question.label,
    unit: filterState.question.unit,
    categories: filterState.question.categories,
  });
  filterState.dataset = null;
  filterState.section = null;
  filterState.question = null;
  close();
};
</script>

<style scoped>
.add-data-drawer-enter-active,
.add-data-drawer-leave-active {
  transition: opacity 0.2s ease;
}
.add-data-drawer-enter-from,
.add-data-drawer-leave-to {
  opacity: 0;
}
.add-data-drawer-enter-active .add-data-drawer-panel,
.add-data-drawer-leave-active .add-data-drawer-panel {
  transition: transform 0.25s ease;
}
.add-data-drawer-enter-from .add-data-drawer-panel,
.add-data-drawer-leave-to .add-data-drawer-panel {
  transform: translateX(100%);
}
</style>
